/** Точка открытия контекстного меню в координатах окна (`clientX`/`clientY`). */
export interface IContextMenuPoint {
    x: number;
    y: number;
}

/** Чем открыто меню: с клавиатуры фокус сразу на первом пункте, указателем — на самом меню. */
export type ContextMenuOpenedBy = 'pointer' | 'keyboard';

type KeyLike = Pick<KeyboardEvent, 'key' | 'shiftKey' | 'ctrlKey' | 'altKey' | 'metaKey'>;

/** Событие, которым открывают меню: `contextmenu` или нажатие Shift+F10 / клавиши меню. */
export type ContextMenuTriggerEvent =
    | Pick<MouseEvent, 'type' | 'clientX' | 'clientY' | 'button' | 'buttons' | 'currentTarget' | 'target'>
    | (KeyLike & Pick<KeyboardEvent, 'type' | 'currentTarget' | 'target'>);

/** Shift+F10 или клавиша меню — открыть контекстное меню элемента в фокусе (в macOS браузер сам его не открывает). */
export const isContextMenuKey = (event: KeyLike): boolean =>
    !event.ctrlKey &&
    !event.altKey &&
    !event.metaKey &&
    (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey));

/** Точка меню у элемента: под ним, у левого края (не дальше середины узкого элемента). */
export const elementMenuPoint = (element: Element): IContextMenuPoint => {
    const rect = element.getBoundingClientRect();
    return { x: rect.left + Math.min(rect.width / 2, 16), y: rect.bottom };
};

/**
 * Меню вызвано с клавиатуры: нажатие клавиши или `contextmenu`, которое браузер прислал на Shift+F10 / клавишу меню, —
 * у такого пустой тип указателя (Pointer Events: событие не от указывающего устройства). Событие без типа указателя
 * (старый движок, синтетическое) — с клавиатуры, только если у него нет координат.
 */
export const isKeyboardContextMenu = (event: ContextMenuTriggerEvent): boolean => {
    if (!('clientX' in event)) return true;
    // у события React тип указателя — только в исходном событии
    const native: Partial<PointerEvent> = (event as { nativeEvent?: Partial<PointerEvent> }).nativeEvent ?? event;
    if (native.pointerType !== undefined) return native.pointerType === '';
    return event.clientX === 0 && event.clientY === 0;
};

const elementOf = (event: ContextMenuTriggerEvent): Element | null => {
    const element = event.currentTarget ?? event.target;
    return element instanceof Element ? element : null;
};

/**
 * Точка меню по событию: указатель — точка курсора; клавиатура — у элемента в фокусе или у цели (браузер, приславший
 * `contextmenu` с клавиатуры, сам ставит точку у элемента; без координат — считаем её сами).
 */
export const contextMenuPoint = (event: ContextMenuTriggerEvent): IContextMenuPoint => {
    if ('clientX' in event && (event.clientX !== 0 || event.clientY !== 0)) {
        return { x: event.clientX, y: event.clientY };
    }
    const active = document.activeElement;
    const element = active && active !== document.body ? active : elementOf(event);
    return element ? elementMenuPoint(element) : { x: 0, y: 0 };
};
